import React, { useState, useEffect } from 'react';
import Dialog from 'src/components/common/Dialog.redesign';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { SelectItem } from 'src/models/common/SelectItem';
import TagContainer from 'src/components/counties/TagContainer';
import Button from 'src/components/controls/forms/redesign/FormButton';
import useFetch from 'use-http';
import { Url } from 'src/api';
import { useSelector } from 'react-redux';
import { IRootState } from 'src/reducers';
import Tag from 'src/models/Tag';
import { FormErrors } from 'src/util/FormErrors';
import { AnyObject, FormApi } from 'final-form';
import TractDetail from 'src/models/tract_detail/TractDetail';
import SurfaceTract from 'src/models/SurfaceTract';

interface ITagsDialogProps {
    onCloseDialog?: () => void;
    action: 'create' | 'edit' | 'delete';
    tractNumber?: number;
    tractId?: number;
    sectionNumber?: number;
    surfaceTractId?: number;
    onUpdateComplete: (nextPage: boolean) => void;
}

const TagsDialog: React.FC<ITagsDialogProps> = (props) => {
    const appContext = useSelector((storeState: IRootState) => storeState.appContext);
    const { get, post, response} = useFetch();
    const [tractId, setTractId] = useState(undefined);
    const [tags, setTags] = useState([]);
    const [tagsLoaded, setTagsLoaded] = useState(false);
    const [cancelled, setCancelled] = useState(false);

    const dialogTitle = () => {
        switch (props.action) {
            case 'create':
                return 'Create Section';
                break;
            case 'edit':
                return 'Edit Project Tags';
                break;
            case 'delete':
                return 'Delete Section';
                break;
            default:
                return '[Error]';
                break;
        }
    };

    const buttonText = () => {
        switch (props.action) {
            case 'create':
                return 'Create Tags';
                break;
            case 'edit':
                return 'Save';
                break;
            case 'delete':
                return 'Delete Tags';
                break;
            default:
                return '[Error]';
                break;
        }
    };

    const updateSurfaceTractTags = async (newTags: Tag[], deleteTags: []) => {
        if (props.surfaceTractId) {
            if (props.surfaceTractId !== undefined && props.sectionNumber !== undefined) {
                let surfaceTract = new SurfaceTract();
                surfaceTract.id  = props.surfaceTractId;
                surfaceTract.addTags = newTags;
                surfaceTract.deleteTags = deleteTags;
                let surfaceTractServiceCallUrl = `v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${props.sectionNumber}/Tracts/surface-tracts/${props.surfaceTractId}/tags`;
                const updateSurfaceTractTagsCall = post(surfaceTractServiceCallUrl, surfaceTract);
                await updateSurfaceTractTagsCall;
            } else {
                return false;
            }
        
            if (response.ok) {
                console.log('updateSurfaceTractTagsCall succeeded');
            } else {
                return false;
            }            
        } else {
            return false;
        }
        return true;
    };        

    const updateTractTags = async (newTags: Tag[], deleteTags: []) => {
        if ((props.tractNumber || props.surfaceTractId) && props.sectionNumber) {
            const tractDetailResult = get(
                `v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${props.sectionNumber}/tracts/${props.tractNumber}/horizons/1`).then((data: TractDetail) => {
                    console.log('getTractDetail', data);

                    return data;
                });

            const existingTags = (await tractDetailResult).tags;

            if (response.ok) {
                let tagsToUpdate: Tag[] = [];
                newTags.forEach(newTag => {
                    if (existingTags.find((tag) => tag === newTag.name) === undefined) {
                        tagsToUpdate.push(newTag);
                    }
                });                

                // Send the tagsToUpdate array off to the service to upate the tags db
                if (tagsToUpdate?.length > 0) {
                    // If this is a tag meant for a tract (not just the project) sectionNumber and tractNumber will be defined
                    if (props.sectionNumber && props.tractNumber) {
                        let tractDetail = new TractDetail();
                        tractDetail.addTags = tagsToUpdate;
                        tractDetail.deleteTags = deleteTags;
                        tractDetail.tractNumber = props.tractNumber;
                        tractDetail.id = props.tractId;
                        const updateTractTagsCall = post(Url.tract.addTagsToTract(appContext), tractDetail);
                        await updateTractTagsCall;
                
                        if (response.ok) {
                            console.log('updateTractTagsCall succeeded');
                        }
                    } else { // else the tags are added to the project ONLY.
                        const updateTagsCall = post(Url.project.addProjectTags(appContext), newTags);
                        await updateTagsCall;
                        
                        if (response.ok) {
                            console.log('tractDetailResult succeeded');
                            if (newTags?.length > 0) {            
                                setTags([...newTags, tags]);
                            }
                        } else {
                            return false;
                        }
                    }
                } else {
                    return false;
                }
            } else {
                return false;
            }
        }
        return true;
    };

    const updateProjectTags = async (newTags: Tag[]) => {
        const updateProjectTagsCall = post(Url.project.addProjectTags(appContext), newTags);

        await updateProjectTagsCall;
        
        if (response.ok) {
            console.log('updateProjectTagsCall succeeded');
            if (newTags?.length > 0) {            
                setTags([...newTags, tags]);
            }
        } else {
            return false;
        }
        return true;
    };

    const loadData = async () => {
        setTractId(props.tractNumber);
        const tagsCall = get(Url.project.getProjectTags(appContext));

        const [tagsData] = [await tagsCall];

        if (response.ok) {
            setTags(tagsData.items);
        }
    };

    useEffect(() => {
        console.log(`Tags Loaded.`);
    }, [tagsLoaded]);

    useEffect(() => {
        if (tags.length > 0) {
            setTagsLoaded(true);
        }
    }, [tags]);
    
    useEffect(() => {
        loadData();
    }, []);

    const handleCancel = () => {
        console.log('Cancelled!!');
        setCancelled(true);
        props.onCloseDialog();
    };

    const submitForm = async (values: AnyObject) => {
        if (props.action === 'edit' && !cancelled) {            
            let addTags = values?.addTags?.length > 0 ? values.addTags : [];
            let deleteTags = values?.deleteTags?.length > 0 ? values.deleteTags : [];

            let updateResult = undefined;
            if (props.sectionNumber !== undefined && props.tractNumber && props.tractId !== undefined) {
                updateResult = await updateTractTags(addTags, deleteTags);
            } else if (props.surfaceTractId !== undefined) {
                updateResult = await updateSurfaceTractTags(addTags, deleteTags);
            } else {
                updateResult = await updateProjectTags(addTags);
            }

            if (updateResult !== undefined) {
                props.onUpdateComplete(false);
            }
            props.onCloseDialog();
        }
    };

    const TagContainerNode = (formApi: FormApi) => {
        return (
            <>
                <TagContainer
                    label="Tags"
                    fieldArrayName="tags"
                    fieldAddArrayName="addTags"
                    fieldDeleteArrayName="deleteTags"
                    initialValueName="tag"
                    form={formApi}
                    isTagCreatable={true}
                    selectTags={tags.length > 0 ? tags.map((t: Tag) => new SelectItem(t.id, t.name)) : new Array<SelectItem>()}
                />
            </>
        );
    };

    const validateForm = () => {
        const errors: FormErrors<{ tagErrors: string}> = {};
        if (tags?.length > 0) {
            for (let index = 0; index < tags?.length; index++) {
                  if (tags[index].name === '') {
                      errors.tagErrors += 'Name must not be empty. Tag[' + index + '} | ';
                  }
            }
        }

        return errors;
    };

    return (
        <Dialog title={dialogTitle()}>
            <Form
                onSubmit={submitForm}
                validate={validateForm}
                initialValues={tractId}
                mutators={{ ...arrayMutators }}
                render={({ handleSubmit, form, errors }) => (
                    <form onSubmit={handleSubmit} className="dlc-form">
                        {TagContainerNode(form)}
                        <Button className="dlc-form-button-default medium primary" type="submit" text={buttonText()} />
                        <div style={{float: 'right'}}>
                            <button className="dlc-form-button-default medium"  type="submit" onClick={handleCancel}>Cancel❌</button>
                        </div>
                    </form>
                )}
            />
        </Dialog>
    );
};

export default TagsDialog;



// WEBPACK FOOTER //
// ./src/components/project/sections/tract/dialogs/TagsDialog.tsx